/**
 * Generic string helper utilities.
 * Lightweight, zero-dependency, pure functions.
 */

/** RFC4122-ish GUID regex (accepts lowercase/uppercase hex) */
const GUID_REGEX =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/** Returns true if value is a valid Date string or Date object (finite timestamp). */
export function isDate(value: unknown): boolean {
  if (value instanceof Date) return !isNaN(value.getTime());
  if (typeof value === 'string') {
    const d = new Date(value);
    return !isNaN(d.getTime());
  }
  return false;
}

/** Returns true if value matches GUID format. */
export function isGuid(value: unknown): boolean {
  return typeof value === 'string' && GUID_REGEX.test(value);
}

/** Returns true if value is null/undefined/empty string OR empty guid. */
export function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' || trimmed === EMPTY_GUID;
  }
  return false;
}

/** Returns true if value is a GUID and not the empty GUID constant. */
export function isValidGuid(value: unknown): boolean {
  return isGuid(value) && value !== EMPTY_GUID;
}

/** Case-insensitive equality against one or more expected values. */
export function isSame(value: unknown, ...expected: string[]): boolean {
  if (typeof value !== 'string' || !expected.length) return false;
  const v = value.trim().toLowerCase();
  for (const exp of expected) {
    if (typeof exp !== 'string') continue;
    if (v === exp.trim().toLowerCase()) return true;
  }
  return false;
}

/** Case-sensitive equality against one or more expected values. */
export function isExactSame(value: unknown, ...expected: string[]): boolean {
  if (typeof value !== 'string' || !expected.length) return false;
  const v = value.trim();
  for (const exp of expected) {
    if (typeof exp !== 'string') continue;
    if (v === exp.trim()) return true;
  }
  return false;
}

export const Strings = {
  GUID_REGEX,
  EMPTY_GUID,
  isDate,
  isGuid,
  isEmpty,
  isValidGuid,
  isSame,
  isExactSame,
};
